import { Link } from "react-router-dom";
import {
  Sparkles, Wallet, PlaneTakeoff, MapPin, Briefcase, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

// Upsell block shown at the bottom of the Quick Check results — Quick
// Check answers only 1 of the 5 layers a real applicant needs. Rendered
// only when there ARE results so it never blocks the empty-state.
export default function UpsellToAdvanced({ user, onSignIn, intendedDegree }) {
  const missing = [
    {
      icon: Wallet,
      title: "Can you afford it?",
      body: "Quick Check ignores tuition, accommodation, food, insurance, deposits and fee growth. Advanced Search compares each university's true total cost against your family's real annual and total budget — and flags scholarships that could close the gap.",
    },
    {
      icon: PlaneTakeoff,
      title: "Can you get the visa?",
      body: "Quick Check doesn't know your passport or residence. Advanced Search checks the sponsor licence, financial-evidence period (Germany's Sperrkonto, UK's 28-day rule, US's I-20 documentation), interview requirements, TB testing, and any prior-refusal complications.",
    },
    {
      icon: MapPin,
      title: "Will you actually enjoy it?",
      body: "Quick Check doesn't weight campus feel, climate, city size, diversity, sport, or distance from home — the things that make three years bearable or brilliant. Advanced Search rebalances the shortlist around what matters to you.",
    },
    {
      icon: Briefcase,
      title: "Will the degree take you somewhere?",
      body: "Quick Check ignores accreditation, placement years, post-study work rights, and whether the qualification is recognised where you want to work. Advanced Search scores every match on career return, not just admission chance.",
    },
  ];

  return (
    <Card data-testid="upsell-to-advanced" className="mt-10 border-stone-200 bg-[#F5F3FF] overflow-hidden">
      <div className="p-6 lg:p-8">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="label-uppercase text-[#4C1D95]">This is 1 of 5 answers</div>
            <h3 className="font-editorial text-2xl sm:text-3xl mt-2 max-w-2xl leading-snug">
              You know your academic chances. You don't yet know if you can
              <span className="italic"> afford </span> it, <span className="italic">reach</span> it,
              or whether it will <span className="italic">take you</span> where you want to go.
            </h3>
          </div>
          <div className="text-right">
            <div className="font-mono text-6xl font-black text-[#4C1D95]">1<span className="text-3xl text-stone-400">/5</span></div>
            <div className="label-uppercase !text-[9px]">layers analysed</div>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {missing.map((m, i) => (
            <div key={i} data-testid={`upsell-layer-${i}`} className="flex gap-3 p-4 bg-white rounded-md border border-stone-200">
              <m.icon className="h-5 w-5 text-[#4C1D95] shrink-0 mt-0.5" />
              <div>
                <div className="font-editorial text-lg leading-tight">{m.title}</div>
                <p className="text-sm text-stone-600 mt-1.5 leading-relaxed">{m.body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          {user ? (
            <Link to="/advanced">
              <Button data-testid="upsell-continue-btn"
                      className="h-auto py-3 px-6 bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md">
                <span className="flex flex-col items-start leading-tight text-left">
                  <span className="text-base font-semibold flex items-center">
                    See the full picture <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                  <span className="text-[11px] font-underground-sc tracking-wider opacity-85 mt-0.5">
                    cost · visa · life · career fit
                  </span>
                </span>
              </Button>
            </Link>
          ) : (
            <Button data-testid="upsell-signin-btn" onClick={onSignIn}
                    className="h-auto py-3 px-6 bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md">
              <span className="flex flex-col items-start leading-tight text-left">
                <span className="text-base font-semibold flex items-center">
                  <Sparkles className="h-4 w-4 mr-2" /> Sign in for the full picture
                </span>
                <span className="text-[11px] font-underground-sc tracking-wider opacity-85 mt-0.5">
                  cost · visa · life · career fit
                </span>
              </span>
            </Button>
          )}
          <div className="text-xs text-stone-500 font-mono">
            {intendedDegree
              ? `Your ${intendedDegree} shortlist will be rebuilt around budget, visa, life & career fit.`
              : "Your shortlist will be rebuilt around budget, visa, life & career fit."}
          </div>
        </div>
      </div>
    </Card>
  );
}
